export type AppLocale = 'en' | 'zh' | 'ja' | 'es' | 'fr' | 'ru';
export type AccountLanguageCode = 'EN' | 'ZH' | 'JP' | 'ES' | 'FR' | 'RU';

export interface LocalizedText {
  en?: string;
  zh?: string;
  ja?: string;
  jp?: string;
  es?: string;
  fr?: string;
  ru?: string;
}

export type MaybeLocalizedText = string | LocalizedText | null | undefined;

export const APP_LOCALE_STORAGE_KEY = 'vadauro-locale';
const LEGACY_LOCALE_STORAGE_KEYS = [
  'vadauro-home-language',
  'vadauro-shop-language',
  'vadauro-measurement-language',
  'vadauro-tryon-upload-language',
  'vadauro-tryon-results-language',
  'vadauro-ai-library-language',
  'vadauro-salon-language',
  'vadauro-login-language',
  'vadauro-create-account-language',
  'vadauro-orders-language',
];

export function normalizeLocale(value: string | null | undefined): AppLocale {
  const normalized = value?.toLowerCase() ?? '';
  if (normalized.startsWith('zh')) {
    return 'zh';
  }
  if (normalized.startsWith('ja')) {
    return 'ja';
  }
  if (normalized.startsWith('es')) {
    return 'es';
  }
  if (normalized.startsWith('fr')) {
    return 'fr';
  }
  if (normalized.startsWith('ru')) {
    return 'ru';
  }
  return 'en';
}

export function toAccountLanguageCode(locale: AppLocale): AccountLanguageCode {
  switch (locale) {
    case 'zh':
      return 'ZH';
    case 'ja':
      return 'JP';
    case 'es':
      return 'ES';
    case 'fr':
      return 'FR';
    case 'ru':
      return 'RU';
    case 'en':
    default:
      return 'EN';
  }
}

export function fromAccountLanguageCode(value: unknown): AppLocale | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toUpperCase().replace(/[^A-Z]/g, '');
  switch (normalized) {
    case 'ZH':
      return 'zh';
    case 'JP':
    case 'JA':
      return 'ja';
    case 'ES':
      return 'es';
    case 'FR':
      return 'fr';
    case 'RU':
      return 'ru';
    case 'EN':
      return 'en';
    default:
      return null;
  }
}

export function bootstrapStoredLocale() {
  if (typeof window === 'undefined') {
    return;
  }

  const existing = window.localStorage.getItem(APP_LOCALE_STORAGE_KEY);
  if (existing) {
    return;
  }

  const legacyValue = LEGACY_LOCALE_STORAGE_KEYS
    .map((key) => window.localStorage.getItem(key))
    .find((value) => value === 'en' || value === 'zh' || value === 'ja' || value === 'es' || value === 'fr' || value === 'ru');

  if (legacyValue) {
    window.localStorage.setItem(APP_LOCALE_STORAGE_KEY, legacyValue);
  }
}

export function persistLocale(locale: AppLocale) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(APP_LOCALE_STORAGE_KEY, locale);
  for (const key of LEGACY_LOCALE_STORAGE_KEYS) {
    window.localStorage.setItem(key, locale);
  }
}

export function resolveLocalizedText(value: MaybeLocalizedText, locale: AppLocale): string {
  if (typeof value === 'string') {
    return value;
  }

  if (!value) {
    return '';
  }

  return value[locale]
    ?? (locale === 'ja' ? value.jp : undefined)
    ?? value.en
    ?? value.zh
    ?? value.ja
    ?? value.jp
    ?? value.es
    ?? value.fr
    ?? value.ru
    ?? '';
}
